import { SEGMENT_FIELDS } from "@/constants/segmentOptions";
import {
  CONDITION_CATEGORIES,
  CATEGORY_BY_ID,
  TAG_VALUE_GROUPS,
  categoryOfCondition,
} from "@/constants/conditionCategories";
import { summarizeSchedule } from "./schedule";
import { isEmptyCondition } from "./segmentFormUtils";

const OP_LABELS = {
  eq: "is",
  neq: "is not",
  in: "is any of",
  nin: "is none of",
  contains: "contains",
  startsWith: "starts with",
  endsWith: "ends with",
  regex: "matches",
  arrayContains: "has all of",
  intersects: "is any of",
  gt: "greater than",
  gte: "at least",
  lt: "less than",
  lte: "at most",
  between: "between",
  exists: "exists",
  timeWindow: "on schedule",
};

export const operatorLabel = (op) => OP_LABELS[op] ?? op;

const TAG_VALUE_LABELS = {};
[...TAG_VALUE_GROUPS.interests, ...TAG_VALUE_GROUPS.demographics].forEach((g) =>
  g.options.forEach((o) => {
    TAG_VALUE_LABELS[o.value] = o.label;
  }),
);

const humanizeToken = (v) =>
  String(v)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();

export const conditionFieldLabel = (condition) => {
  if (condition.field === "tags") {
    const cat = categoryOfCondition(condition);
    return CATEGORY_BY_ID[cat]?.label ?? "Device tags";
  }
  return (
    SEGMENT_FIELDS.find((f) => f.value === condition.field)?.label ??
    condition.field
  );
};

const resolveOne = (field, v, cfg) => {
  if (field === "tags") return TAG_VALUE_LABELS[v] ?? humanizeToken(v);
  const opt = cfg?.[field]?.options?.find((o) => String(o.value) === String(v));
  return opt?.label ?? String(v);
};

export const conditionValueText = (condition, cfg) => {
  const { field, value, op } = condition;
  if (op === "timeWindow") return summarizeSchedule(value) ?? "any time";
  if (op === "exists") return value === true ? "present" : "absent";
  if (value === undefined || value === null || value === "") return "—";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    return value.map((v) => resolveOne(field, v, cfg)).join(", ");
  }
  return resolveOne(field, value, cfg);
};

export const groupsInDefinition = (definition = {}) => {
  const ids = new Set();
  ["all", "any", "none"].forEach((g) =>
    (definition[g] ?? []).forEach((c) => ids.add(categoryOfCondition(c))),
  );
  return CONDITION_CATEGORIES.filter((c) => ids.has(c.id));
};

const conditionChip = (c, cfg) => {
  if (c.op === "timeWindow") return conditionValueText(c, cfg);
  if (c.field === "sessionDuration") {
    const sym = c.op === "lte" ? "≤" : "≥";
    return `${sym} ${c.value}s`;
  }
  return conditionValueText(c, cfg);
};

export const groupSummaries = (definition = {}, cfg) => {
  const order = [];
  const byCat = {};
  ["all", "any", "none"].forEach((g) =>
    (definition[g] ?? []).forEach((c) => {
      const cat = categoryOfCondition(c);
      if (!byCat[cat]) {
        byCat[cat] = [];
        order.push(cat);
      }
      byCat[cat].push(c);
    }),
  );
  return order.map((catId) => {
    const chips = (byCat[catId] || [])
      .filter((c) => !isEmptyCondition(c))
      .map((c) => conditionChip(c, cfg))
      .filter((s) => s && s !== "—");
    return { category: CATEGORY_BY_ID[catId], summary: chips.join(", ") };
  });
};
